import React, { useEffect, useState } from "react"
import { useToaster } from "@hellocontento/maillard"
import ReactLoading from "react-loading"
import { Globe, Image, Plus, Upload, X } from "styled-icons/feather"
import { callApi } from "utils/ContentoApi"
import { theme } from "theme"

import * as styles from "../styles"
import { Picture, PictureOrigin } from "../types"
import { fetchPictures } from "../picturesApi"
import { usePictureUpload } from "../usePictureUpload"
import { useRotatingMessage } from "../useRotatingMessage"

const IMPORT_STATUS_MESSAGES = [
  "Reading your website…",
  "Looking for photos…",
  "Picking the best pictures…",
  "Detecting faces…",
  "Almost there…",
]
const IMPORT_PLACEHOLDER_COUNT = 3
const IMPORT_POLL_INTERVAL_MS = 2000

const ORIGIN_BADGES: Record<PictureOrigin, { Icon: typeof Globe; label: string }> = {
  upload: { Icon: Upload, label: "Uploaded" },
  website: { Icon: Globe, label: "Added from your website" },
}

type WebsiteImportStatus =
  | { status: "running" }
  | { status: "completed"; addedCount: number; failedCount: number }
  | { status: "idle" }

type ToastKind = "success" | "info" | "error"

interface Props {
  accountId: string
  profileId: string
  isWebsiteProfile: boolean
}

const countPictures = (count: number) => `${count} picture${count === 1 ? "" : "s"}`

const describeImportResult = ({ addedCount, failedCount }: { addedCount: number; failedCount: number }): [string, ToastKind] => {
  if (addedCount > 0 && failedCount > 0) {
    return [`Added ${countPictures(addedCount)} from your website. ${failedCount} more couldn't be added, try again later.`, "info"]
  }
  if (failedCount > 0) return [`${countPictures(failedCount)} couldn't be added. Try again later.`, "error"]
  if (addedCount > 0) return [`Added ${countPictures(addedCount)} from your website.`, "success"]
  return ["No new pictures found on your website.", "info"]
}

const fetchWebsiteImportStatus = (accountId: string, profileId: string): Promise<WebsiteImportStatus> =>
  callApi({ method: "get", url: `content-factory/accounts/${accountId}/profiles/${profileId}/pictures/import-from-website` }, true, true)

const PicturesRow = ({ accountId, profileId, isWebsiteProfile }: Props) => {
  const addToast = useToaster()
  const [pictures, setPictures] = useState<Picture[]>([])
  const [isImporting, setIsImporting] = useState(false)
  const { uploadingCount, statusMessage: uploadStatusMessage, isDragging, dropZoneProps, fileInputProps, openFilePicker } = usePictureUpload({
    accountId,
    profileId,
    disabled: isImporting,
    onPicturesAdded: (createdPictures) => setPictures((previous) => [...createdPictures, ...previous]),
  })
  const importStatusMessage = useRotatingMessage(IMPORT_STATUS_MESSAGES, isImporting)

  const placeholderCount = isImporting ? IMPORT_PLACEHOLDER_COUNT : uploadingCount
  const statusMessage = isImporting ? importStatusMessage : uploadStatusMessage

  useEffect(() => {
    fetchPictures(accountId, profileId)
      .then(setPictures)
      .catch((err: unknown) => {
        console.error("Failed to fetch pictures", { accountId, profileId, err })
      })
  }, [accountId, profileId])

  // A new website profile starts an import on its own, and an import keeps running across page reloads.
  useEffect(() => {
    if (!isWebsiteProfile) return
    fetchWebsiteImportStatus(accountId, profileId)
      .then(({ status }) => {
        if (status === "running") setIsImporting(true)
      })
      .catch((err: unknown) => {
        console.error("Failed to fetch the website picture import", { accountId, profileId, err })
      })
  }, [accountId, profileId, isWebsiteProfile])

  useEffect(() => {
    if (!isImporting) return

    let stopped = false
    let timeoutId: number | undefined

    const poll = async () => {
      try {
        const importStatus = await fetchWebsiteImportStatus(accountId, profileId)
        if (stopped) return
        if (importStatus.status === "completed") {
          setPictures(await fetchPictures(accountId, profileId))
          addToast(...describeImportResult(importStatus))
          setIsImporting(false)
          return
        }
        // Failed imports are removed from the queue, so a run that disappears has failed.
        if (importStatus.status === "idle") {
          addToast("Failed to add pictures from website", "error")
          setIsImporting(false)
          return
        }
      } catch (err) {
        console.error("Failed to poll the website picture import", { accountId, profileId, err })
      }
      if (!stopped) timeoutId = window.setTimeout(poll, IMPORT_POLL_INTERVAL_MS)
    }

    timeoutId = window.setTimeout(poll, IMPORT_POLL_INTERVAL_MS)
    return () => {
      stopped = true
      window.clearTimeout(timeoutId)
    }
  }, [accountId, profileId, isImporting, addToast])

  // Polling starts only once the job is queued, or the first poll could find no job yet and report a failure.
  const importFromWebsite = async () => {
    try {
      await callApi(
        {
          method: "post",
          url: `content-factory/accounts/${accountId}/profiles/${profileId}/pictures/import-from-website`,
        },
        true,
        true,
      )
      setIsImporting(true)
    } catch (err) {
      console.error("Failed to add pictures from website", { accountId, profileId, err })
      addToast("Failed to add pictures from website", "error")
    }
  }

  const handleDelete = async (picture: Picture) => {
    try {
      await callApi(
        {
          method: "delete",
          url: `content-factory/accounts/${accountId}/profiles/${profileId}/pictures/${picture.id}`,
        },
        true,
        true,
      )
      setPictures((previous) => previous.filter(({ id }) => id !== picture.id))
    } catch (err) {
      console.error("Failed to delete picture", { accountId, profileId, pictureId: picture.id, err })
      addToast("Failed to delete picture", "error")
    }
  }

  return (
    <styles.ProfileSection>
      {pictures.length === 0 && placeholderCount === 0 ? (
        <styles.PicturesEmptyState
          $isDragging={isDragging}
          {...dropZoneProps}
          onClick={openFilePicker}
        >
          <styles.PicturesEmptyIcon>
            <Image size={20} />
          </styles.PicturesEmptyIcon>
          <styles.PicturesEmptyText>
            <styles.PicturesEmptyTitle>No pictures yet</styles.PicturesEmptyTitle>
            <styles.PicturesEmptyHint>
              Add pictures for Create to use in your posts.
            </styles.PicturesEmptyHint>
          </styles.PicturesEmptyText>
          <styles.PicturesEmptyActions>
            <styles.ChooseFilesButton
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                openFilePicker()
              }}
            >
              <Plus size={16} />
              Choose pictures
            </styles.ChooseFilesButton>
            {isWebsiteProfile && (
              <styles.ChooseFilesButton
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  importFromWebsite()
                }}
              >
                <Globe size={16} />
                Add pictures from website
              </styles.ChooseFilesButton>
            )}
          </styles.PicturesEmptyActions>
        </styles.PicturesEmptyState>
      ) : (
        <styles.PicturesDropZone $isDragging={isDragging} {...dropZoneProps} data-testid="pictures-drop-zone">
          <styles.BrandCarousel data-testid="pictures-carousel">
            <styles.AddSlide
              type="button"
              onClick={openFilePicker}
              disabled={placeholderCount > 0}
              title="Add pictures"
            >
              <Plus size={24} />
            </styles.AddSlide>
            {isWebsiteProfile && (
              <styles.AddSlide
                type="button"
                onClick={importFromWebsite}
                disabled={placeholderCount > 0}
                title="Add pictures from website"
              >
                <Globe size={22} />
              </styles.AddSlide>
            )}
            {Array.from({ length: placeholderCount }, (_, index) => (
              <styles.PictureUploadingSlide key={`uploading-${index}`}>
                <ReactLoading type="spin" color={theme.colors.text02} width={32} height={32} />
                <styles.PictureUploadingLabel>{statusMessage}</styles.PictureUploadingLabel>
              </styles.PictureUploadingSlide>
            ))}
            {pictures.map((picture) => {
              const originBadge = ORIGIN_BADGES[picture.origin]
              return (
                <styles.SlideWithDelete key={picture.id}>
                  <styles.PictureSlide src={picture.url} alt="" loading="lazy" />
                  {originBadge && (
                    <styles.PictureOriginBadge role="img" aria-label={originBadge.label} title={originBadge.label}>
                      <originBadge.Icon size={15} />
                    </styles.PictureOriginBadge>
                  )}
                  <styles.PictureDeleteButton
                    type="button"
                    onClick={() => handleDelete(picture)}
                    title="Delete this picture"
                  >
                    <X size={12} strokeWidth={2.5} />
                  </styles.PictureDeleteButton>
                </styles.SlideWithDelete>
              )
            })}
          </styles.BrandCarousel>
        </styles.PicturesDropZone>
      )}
      <input {...fileInputProps} />
    </styles.ProfileSection>
  )
}

export default PicturesRow
